import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Terminal } from "lucide-react";
import { useLang } from "@/i18n";

const NeuralCanvas = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let w, h;
    const mouse = { x: 0.5, y: 0.5 };

    const resize = () => {
      w = canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      h = canvas.height = canvas.offsetHeight * window.devicePixelRatio;
    };
    resize();
    window.addEventListener("resize", resize);

    const N = 70;
    const nodes = Array.from({ length: N }, () => ({
      x: Math.random(),
      y: Math.random(),
      vx: (Math.random() - 0.5) * 0.0006,
      vy: (Math.random() - 0.5) * 0.0006,
    }));

    const onMove = (e) => {
      mouse.x = e.clientX / window.innerWidth;
      mouse.y = e.clientY / window.innerHeight;
    };
    window.addEventListener("mousemove", onMove);

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      const px = (mouse.x - 0.5) * 40 * window.devicePixelRatio;
      const py = (mouse.y - 0.5) * 40 * window.devicePixelRatio;

      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > 1) n.vx *= -1;
        if (n.y < 0 || n.y > 1) n.vy *= -1;
      }

      for (let i = 0; i < N; i++) {
        for (let j = i + 1; j < N; j++) {
          const a = nodes[i];
          const b = nodes[j];
          const dx = (a.x - b.x) * w;
          const dy = (a.y - b.y) * h;
          const d = Math.hypot(dx, dy);
          const max = w * 0.12;
          if (d < max) {
            ctx.strokeStyle = `rgba(29, 78, 216, ${(1 - d / max) * 0.14})`;
            ctx.lineWidth = window.devicePixelRatio;
            ctx.beginPath();
            ctx.moveTo(a.x * w + px, a.y * h + py);
            ctx.lineTo(b.x * w + px, b.y * h + py);
            ctx.stroke();
          }
        }
      }

      for (const n of nodes) {
        ctx.fillStyle = "rgba(29, 78, 216, 0.4)";
        ctx.beginPath();
        ctx.arc(n.x * w + px, n.y * h + py, 1.6 * window.devicePixelRatio, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  return <canvas ref={ref} className="absolute inset-0 h-full w-full" aria-hidden="true" />;
};

const scrollTo = (hash) => {
  if (window.__lenis) window.__lenis.scrollTo(hash, { offset: -72, duration: 1.4 });
  else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
};

const ServicesTable = () => {
  const { t } = useLang();
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}
      data-testid="hero-services-table"
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.08)]"
    >
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-6 py-4">
        <span className="font-mono-alt text-[11px] uppercase tracking-[0.25em] text-slate-500">
          {t.hero.tableTitle}
        </span>
        <span className="font-mono-alt text-[11px] uppercase tracking-[0.25em] text-blue-700">
          {t.hero.tableCount}
        </span>
      </div>
      <div className="grid grid-cols-1 divide-y divide-slate-200 sm:grid-cols-2 sm:divide-x sm:divide-y-0">
        {t.hero.groups.map((g, gi) => (
          <div key={g.group} data-testid={`hero-services-group-${gi}`}>
            <div className="flex items-center gap-2 px-6 pb-2 pt-5">
              <span className={`h-2 w-2 rounded-full ${gi === 0 ? "bg-blue-700" : "bg-teal-500"}`} />
              <span className="font-display text-sm font-bold uppercase tracking-wider text-slate-900">
                {g.group}
              </span>
            </div>
            <ul className="divide-y divide-slate-100">
              {g.items.map((item, i) => (
                <li
                  key={item}
                  data-testid={`hero-service-item-${gi}-${i}`}
                  className="flex items-center gap-3 px-6 py-3 text-sm text-slate-700 transition-colors duration-200 hover:bg-slate-50"
                >
                  <span className="font-mono-alt text-[10px] text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export const Hero = () => {
  const { t, lang } = useLang();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return (
    <section
      id="top"
      ref={sectionRef}
      data-testid="hero-section"
      className="noise-overlay relative flex min-h-screen items-center overflow-hidden"
    >
      <NeuralCanvas />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 50% 40%, rgba(29,78,216,0.06), transparent 70%), radial-gradient(ellipse 40% 40% at 80% 80%, rgba(13,148,136,0.08), transparent 70%)",
        }}
      />

      <motion.div
        style={isDesktop ? { y: contentY, opacity: contentOpacity } : undefined}
        className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-4 pb-24 pt-36 sm:px-6 lg:grid-cols-12 lg:gap-12 lg:px-12"
      >
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mb-8 inline-flex items-center gap-3 rounded-full border border-blue-200 bg-blue-50 px-4 py-2"
            data-testid="hero-eyebrow"
          >
            <Terminal className="h-3.5 w-3.5 text-blue-700" />
            <span className="font-mono-alt text-[11px] uppercase tracking-[0.25em] text-blue-700">
              {t.hero.eyebrow}
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-display max-w-5xl text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl xl:text-7xl"
          >
            {t.hero.lines.map((line, i) => (
              <span key={`${lang}-${i}`} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "text-glow-gradient" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.45 + i * 0.16, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-8 max-w-2xl text-base font-normal leading-relaxed text-slate-700 sm:text-lg"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.3 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("#contact")}
              className="group flex items-center gap-2 rounded-full bg-blue-700 px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-800 hover:shadow-[0_12px_32px_rgba(29,78,216,0.28)]"
            >
              {t.hero.ctaPrimary}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("#services")}
              className="rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-slate-800 transition-all duration-300 hover:-translate-y-0.5 hover:border-blue-600 hover:text-blue-700"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.7 }}
            className="font-mono-alt mt-14 flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-slate-500"
          >
            <span className="h-px w-10 bg-slate-300" />
            {t.hero.footerLine}
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <ServicesTable />
        </div>
      </motion.div>
    </section>
  );
};
